import clsx from 'clsx';
import AspectRatioContainer from 'components/about-us/AspectRatioContainer';
import ImageWithFallback from 'components/ImageWithFallback';
import { referralsFolder } from 'constants/Referrals';
import { useMemo } from 'react';
import {
  getRandomMascotAvatar,
  resolveUserAvatar,
} from 'utils/dashboard/avatar';
import styles from './AvatarWithAccessory.module.css';
import { useUIStore } from 'hooks/stores/useUIStore';

interface AvatarWithAccessoryProps {
  /** The optional avatarUrl. An undefined avatarUrl does not imply the avatar is not present,
   * as our data structure for a user allows their avatar to be empty. Empty avatars will be
   * replaced by a randomised avatar
   */
  avatarUrl?: string;
  accessorySrc: string;
  className?: string;
  accessoryClassName?: string;
  inactive?: boolean;
  /** Whether this avatar on its accessory should be rotated slightly (e.g. they are relaxing on
  their rubber ring */
  relaxing?: boolean;
  /** If true, this avatar will render as a button that shows the invite modal when clicked */
  isEmptySlot?: boolean;
}

export const AvatarWithAccessory = ({
  avatarUrl,
  accessorySrc,
  accessoryClassName,
  inactive,
  relaxing,
  isEmptySlot,
}: AvatarWithAccessoryProps) => {
  const randomAvatar = useMemo(() => getRandomMascotAvatar(), []);
  const uiStore = useUIStore();

  const resolvedAvatar = resolveUserAvatar({
    avatarLocation: avatarUrl,
    randomAvatar,
  });

  // The avatar may be clickable, so we need to either render a button or a div element
  const AvatarElementType = isEmptySlot ? 'button' : 'div';
  const extraProps = isEmptySlot
    ? { onClick: () => uiStore.setShowInviteFriendsModal(true) }
    : undefined;

  return (
    <div className={clsx(styles.root)}>
      <AspectRatioContainer width={1} height={1}>
        <AvatarElementType
          {...extraProps}
          className={clsx(
            styles.avatarContainer,
            inactive && styles.avatarInactive,
            relaxing && styles.relaxingAvatar,
          )}
        >
          {!isEmptySlot ? (
            <ImageWithFallback
              layout="fill"
              src={resolvedAvatar}
              objectFit="cover"
            />
          ) : (
            <ImageWithFallback
              layout="fill"
              src={`${referralsFolder}/add-friend-icon.svg`}
            />
          )}
        </AvatarElementType>
      </AspectRatioContainer>
      <div className={clsx(styles.accessory, accessoryClassName)}>
        <ImageWithFallback layout="fill" src={accessorySrc} />
      </div>
    </div>
  );
};
